import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { authQueryKeys } from "@/hooks/useAuth";
import {
  getMeContextService,
  getMeService,
  loginService,
  registerService,
} from "@/services/auth.service";
import type {
  AuthUser,
  MeContextResponse,
  RegisterFormData,
} from "@/types/authentication.types";
import {
  getAuthToken,
  setAuthToken,
} from "@/utils/local-storage.utils";

interface AuthContextValue {
  user: AuthUser | null;
  context: MeContextResponse | null;
  loading: boolean;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (payload: RegisterFormData) => Promise<void>;
  logout: () => void;
  refreshContext: () => Promise<MeContextResponse | null>;
  installSession: (token: string, user: AuthUser) => Promise<MeContextResponse | null>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [token, setToken] = useState<string | null>(() => getAuthToken());

  const userQuery = useQuery({
    queryKey: authQueryKeys.me(),
    queryFn: getMeService,
    enabled: Boolean(token),
    retry: false,
  });

  const contextQuery = useQuery({
    queryKey: authQueryKeys.meContext(),
    queryFn: getMeContextService,
    enabled: Boolean(token),
    retry: false,
    refetchInterval: token ? 90_000 : false,
  });

  const clearSession = useCallback(() => {
    void queryClient.cancelQueries();
    queryClient.clear();
    localStorage.clear();
    sessionStorage.clear();
    toast.dismiss();
    setToken(null);
  }, [queryClient]);

  useEffect(() => {
    if (token && userQuery.isError) clearSession();
  }, [clearSession, token, userQuery.isError]);

  const installSession = useCallback(async (nextToken: string, user: AuthUser) => {
    setAuthToken(nextToken);
    queryClient.setQueryData(authQueryKeys.me(), user);
    setToken(nextToken);

    try {
      return await queryClient.fetchQuery({
        queryKey: authQueryKeys.meContext(),
        queryFn: getMeContextService,
        staleTime: 0,
      });
    } catch {
      return null;
    }
  }, [queryClient]);

  const login = useCallback(async (email: string, password: string) => {
    const { data, message } = await loginService({ email, password });
    if (!data) throw new Error(message);
    await installSession(data.token, data.user);
  }, [installSession]);

  const register = useCallback(async (payload: RegisterFormData) => {
    const { data, message } = await registerService(payload);
    if (!data) throw new Error(message);
    await installSession(data.token, data.user);
  }, [installSession]);

  const logout = useCallback(() => {
    clearSession();
  }, [clearSession]);

  const refreshContext = useCallback(async () => {
    if (!token) return null;
    const result = await contextQuery.refetch();
    return result.data ?? null;
  }, [contextQuery, token]);

  const isLoading = Boolean(token) && (userQuery.isPending || contextQuery.isPending);
  const value = useMemo<AuthContextValue>(() => ({
    user: token ? (userQuery.data ?? null) : null,
    context: token ? (contextQuery.data ?? null) : null,
    loading: isLoading,
    isLoading,
    isAuthenticated: Boolean(token && userQuery.data && !userQuery.isError),
    login,
    register,
    logout,
    refreshContext,
    installSession,
  }), [
    contextQuery.data,
    installSession,
    isLoading,
    login,
    logout,
    refreshContext,
    register,
    token,
    userQuery.data,
    userQuery.isError,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within an AuthProvider");
  return context;
}
